import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiPlay, FiPause, FiVolume2, FiVolumeX, FiMaximize, FiExternalLink } from 'react-icons/fi';

const VideoPlayer = ({ 
  src, 
  poster, 
  title, 
  externalUrl = null,
  className = "",
  autoplay = false,
  controls = true 
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [showControls, setShowControls] = useState(false);
  const [videoError, setVideoError] = useState(false);

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleMuteToggle = () => {
    setIsMuted(!isMuted);
  };

  const handleVideoError = () => {
    setVideoError(true);
  };

  // If there's an external URL, show a link to it
  if (externalUrl) {
    return (
      <div className={`relative group ${className}`}>
        <div className="relative w-full h-48 bg-gray-900 flex items-center justify-center rounded-lg overflow-hidden">
          <img
            src={poster || '/placeholder-video.jpg'}
            alt={title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
            <a
              href={externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-accent-500 hover:bg-accent-600 text-white px-6 py-3 rounded-full transition-colors duration-200"
            >
              <FiExternalLink size={20} />
              <span>Watch on External Platform</span>
            </a>
          </div>
        </div>
        {title && (
          <p className="mt-2 text-sm text-primary-600 text-center">{title}</p>
        )}
      </div>
    );
  }

  // If video failed to load, show error message
  if (videoError) {
    return (
      <div className={`relative ${className}`}>
        <div className="w-full h-48 bg-gray-200 flex flex-col items-center justify-center rounded-lg">
          <p className="text-gray-500 mb-2">Video unavailable</p>
          <p className="text-sm text-gray-400 text-center">
            This video file is too large or unavailable.<br />
            Consider compressing it or hosting externally.
          </p>
        </div>
        {title && (
          <p className="mt-2 text-sm text-primary-600 text-center">{title}</p>
        )}
      </div>
    );
  }

  return (
    <div 
      className={`relative group ${className}`}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
    >
      <div className="relative w-full h-48 bg-gray-900 rounded-lg overflow-hidden">
        <video
          src={src}
          poster={poster}
          className="w-full h-full object-cover"
          muted={isMuted}
          autoPlay={autoplay}
          loop
          onError={handleVideoError}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
        />
        
        {/* Overlay Controls */}
        <div className={`absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center transition-opacity duration-200 ${
          showControls || !isPlaying ? 'opacity-100' : 'opacity-0'
        }`}>
          <button
            onClick={handlePlayPause}
            className="bg-black bg-opacity-50 text-white p-4 rounded-full hover:bg-opacity-75 transition-colors duration-200"
          >
            {isPlaying ? <FiPause size={24} /> : <FiPlay size={24} />}
          </button>
        </div>

        {/* Bottom Controls */}
        {controls && (
          <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4 transition-opacity duration-200 ${
            showControls ? 'opacity-100' : 'opacity-0'
          }`}>
            <div className="flex items-center justify-between text-white">
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleMuteToggle}
                  className="hover:text-accent-400 transition-colors duration-200"
                >
                  {isMuted ? <FiVolumeX size={20} /> : <FiVolume2 size={20} />}
                </button>
                <span className="text-sm">
                  {isPlaying ? 'Playing' : 'Paused'}
                </span>
              </div>
              <button className="hover:text-accent-400 transition-colors duration-200">
                <FiMaximize size={20} />
              </button>
            </div>
          </div>
        )}
      </div>
      
      {title && (
        <p className="mt-2 text-sm text-primary-600 text-center">{title}</p>
      )}
    </div>
  );
};

export default VideoPlayer;
